import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { RefreshCw, AppWindow, Globe, Lock, Users, UserCheck, ExternalLink } from 'lucide-react'
import { listAllApps, type AdminApp } from '../../lib/api'
import { getAdminAppsPath } from '../../lib/adminRoutes'
// Admin apps overview - shows all apps across all users
export default function AdminWorkspaces() {
  const navigate = useNavigate()
  const [apps, setApps] = useState<AdminApp[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadApps = useCallback(async () => {
    try {
      setError(null)
      const data = await listAllApps()
      setApps(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load apps')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadApps()
  }, [loadApps])

  function getStatusColor(status: string) {
    switch (status) {
      case 'running':
        return 'bg-green-500/20 text-green-500'
      case 'starting':
        return 'bg-blue-500/20 text-blue-500'
      case 'stopped':
      case 'exited':
        return 'bg-muted text-muted-foreground'
      case 'error':
        return 'bg-red-500/20 text-red-500'
      default:
        return 'bg-muted text-muted-foreground'
    }
  }

  function formatRelativeTime(dateStr: string | null) {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }

  function formatDate(dateStr: string | null) {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr)
    return date.toLocaleString()
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold mb-2">Apps</h1>
          <p className="text-muted-foreground">View all user apps across the platform</p>
        </div>
        <button
          onClick={loadApps}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : apps.length === 0 ? (
        <div className="text-center py-12">
          <AppWindow className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No apps yet</h3>
          <p className="text-muted-foreground">
            Apps are created when users create new workspaces.
          </p>
        </div>
      ) : (
        <>
        {/* Mobile: card list */}
        <div className="md:hidden space-y-3">
          {apps.map((app) => {
            const AccessIcon = app.access_level === 'public' ? Globe :
              app.access_level === 'platform' ? Users :
              app.access_level === 'specific' ? UserCheck : Lock
            return (
              <div
                key={`m-${app.id}`}
                className="border border-border rounded-xl p-4 bg-card cursor-pointer hover:bg-secondary/30"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('a')) return
                  navigate(getAdminAppsPath({ query: app.name }))
                }}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="relative shrink-0">
                      <div className={`w-2 h-2 rounded-full ${app.status === 'running' ? 'bg-green-500' : app.status === 'starting' ? 'bg-amber-500' : 'bg-gray-500'}`} />
                      {app.status === 'running' && (
                        <div className="absolute inset-0 w-2 h-2 rounded-full bg-green-500 animate-ping" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{app.display_name || app.name}</p>
                      <p className="text-xs text-muted-foreground font-mono truncate">/a/{app.name}/</p>
                    </div>
                  </div>
                  <a
                    title="Open this app"
                    href={app.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0 p-2 rounded-lg hover:bg-secondary transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
                <div className="flex items-center gap-3 mb-2 text-xs">
                  <span className={`px-2 py-0.5 rounded ${getStatusColor(app.status)}`}>{app.status}</span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <AccessIcon className="w-3 h-3" />
                    <span className="capitalize">{app.access_level}</span>
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <Link
                    to={`/admin/users/${app.user_id}`}
                    className="truncate hover:text-primary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {app.user_email}
                  </Link>
                  <span className="shrink-0 ml-2">{formatRelativeTime(app.last_accessed)}</span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block rounded-xl border border-border overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-secondary">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium">App</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Owner</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Access</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Last Accessed</th>
                <th className="text-right px-4 py-3 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {apps.map((app) => {
                const AccessIcon = app.access_level === 'public' ? Globe :
                  app.access_level === 'platform' ? Users :
                  app.access_level === 'specific' ? UserCheck : Lock

                return (
                  <tr
                    key={app.id}
                    className="hover:bg-secondary/50 cursor-pointer"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('a')) return
                      navigate(getAdminAppsPath({ query: app.name }))
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className={`w-2 h-2 rounded-full ${app.status === 'running' ? 'bg-green-500' : app.status === 'starting' ? 'bg-amber-500' : 'bg-gray-500'}`} />
                          {app.status === 'running' && (
                            <div className={`absolute inset-0 w-2 h-2 rounded-full bg-green-500 animate-ping`} />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{app.display_name || app.name}</p>
                          <p className="text-xs text-muted-foreground font-mono">/a/{app.name}/</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/users/${app.user_id}`}
                        className="hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <p className="font-medium text-sm">{app.user_email?.split('@')[0] || `User ${app.user_id}`}</p>
                        <p className="text-xs text-muted-foreground">{app.user_email}</p>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      
                        <span title={`App is currently ${app.status}`} className={`px-2 py-1 rounded text-xs ${getStatusColor(app.status)}`}>
                          {app.status}
                        </span>
                      
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <AccessIcon className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-sm capitalize">{app.access_level}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm">{formatRelativeTime(app.last_accessed)}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(app.last_accessed)}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        
                          <a title="Open this app in a new tab"
                            href={app.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="p-2 rounded-lg hover:bg-secondary transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  )
}
